import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiCall } from '../hooks/useApiCall';
import { User, type BackendUser } from '../types';
import { authLogger } from '../utils/logger';
import { isMobileUserAgent } from '../utils/mobileUtils';
import { getServiceHeaders } from '../utils/serviceUtils';
import { sessionGet } from '../utils/storage';
import {
  buildYandexStartUrl,
  clearYandexCallbackFromUrl,
  readYandexCallback,
  YANDEX_API_BASE,
  YANDEX_AUTH_MODE,
  type YandexCallback,
} from '../utils/yandex';

interface YandexAuthProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

/** Логотип: белая «Я» на фирменном красном. */
const YandexMark: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-[#FC3F1D] flex items-center justify-center flex-shrink-0 ${className}`}>
    <span className="font-bold leading-none text-on-accent">Я</span>
  </div>
);

function backendUserToYandexUser(backend: BackendUser): User {
  return {
    id: backend.connection?.yandex || backend.uid || 'unknown',
    name: backend.name && backend.surname ? `${backend.name} ${backend.surname}` : backend.name || 'Яндекс ID',
    avatar: backend.photo || '',
    platform: 'yandex',
  };
}

const YandexAuth: React.FC<YandexAuthProps> = ({ onSuccess, onCancel }) => {
  const { login, setError, sendToParent, redirectToReturnUrl } = useAuth();
  const { execute } = useApiCall();

  // Ответ Яндекса/шлюза читаем один раз, до того как почистим адресную строку.
  const callbackRef = useRef<YandexCallback | null>(readYandexCallback());
  const startedRef = useRef(false);

  const [status, setStatus] = useState<'idle' | 'redirecting' | 'exchanging'>(
    callbackRef.current?.ssmmToken || callbackRef.current?.yandexToken ? 'exchanging' : 'idle',
  );
  const [errorText, setErrorText] = useState<string | null>(callbackRef.current?.error ?? null);

  const finishLogin = useCallback(
    async (token: string) => {
      const backendUser = (await execute({
        url: `${YANDEX_API_BASE}/user`,
        method: 'GET',
        headers: {
          'ssmm-token': token,
          ...getServiceHeaders(),
        },
      })) as BackendUser | null;

      if (!backendUser) {
        throw new Error('Не удалось загрузить профиль пользователя');
      }

      login(backendUserToYandexUser(backendUser), token, backendUser);

      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

      // Адрес чистим только после успеха: при ошибке пользователь может обновить страницу.
      clearYandexCallbackFromUrl();

      if (returnUrl && isMobileUserAgent()) {
        redirectToReturnUrl(token);
        return;
      }

      onSuccess?.();
    },
    [execute, login, onSuccess, redirectToReturnUrl],
  );

  /** Токен Яндекса → ssmm-токен (нужно только в direct-режиме). */
  const exchangeYandexToken = useCallback(
    async (yandexToken: string): Promise<string> => {
      const result = await execute({
        url: `${YANDEX_API_BASE}/oauth/yandex/authorize`,
        method: 'POST',
        headers: {
          ...getServiceHeaders(),
        },
        data: { access_token: yandexToken },
      });

      if (!result?.token) {
        throw new Error('Бэкенд не вернул токен по ответу Яндекса');
      }
      return result.token;
    },
    [execute],
  );

  useEffect(() => {
    const callback = callbackRef.current;
    if (!callback || startedRef.current) return;
    startedRef.current = true;

    if (callback.error) {
      authLogger.error('Яндекс ID вернул ошибку', { error: callback.error, mode: callback.mode });
      setStatus('idle');
      clearYandexCallbackFromUrl();
      return;
    }

    const run = async () => {
      try {
        setStatus('exchanging');
        setErrorText(null);
        setError(null);

        const token = callback.ssmmToken || (await exchangeYandexToken(callback.yandexToken || ''));
        authLogger.success('Яндекс ID: токен получен', { mode: callback.mode });
        await finishLogin(token);
      } catch (e: unknown) {
        const message =
          (e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
            ? (e as Error).message
            : null) || 'Не удалось завершить вход через Яндекс ID';

        authLogger.error('Яндекс ID: вход не завершён', { error: message });
        setErrorText(message);
        setError(message);
        setStatus('idle');
        // Повтор возможен только с новым кодом/токеном — старый в адресе уже не нужен.
        clearYandexCallbackFromUrl();
        sendToParent({ type: 'AUTH_ERROR', error: message });
      }
    };

    void run();
  }, [exchangeYandexToken, finishLogin, sendToParent, setError]);

  const handleStart = () => {
    setErrorText(null);
    setError(null);
    setStatus('redirecting');

    const url = buildYandexStartUrl();
    authLogger.info('Яндекс ID: уходим на авторизацию', { mode: YANDEX_AUTH_MODE, url });
    window.location.assign(url);
  };

  const busy = status !== 'idle';

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={() => onCancel?.()}
          className="flex items-center mb-6 transition-colors text-zinc-500 hover:text-zinc-300"
          type="button"
        >
          <svg className="mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>

        <div className="flex items-center mb-2 space-x-3">
          <YandexMark className="w-10 h-10 rounded-xl text-lg" />
          <div>
            <h1 className="text-lg font-semibold text-white">Яндекс ID</h1>
            <p className="text-xs text-zinc-500">Вход по аккаунту Яндекса</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {errorText && (
          <div className="p-3 mb-4 rounded-xl border bg-red-500/10 border-red-500/30">
            <p className="text-sm text-red-400">{errorText}</p>
          </div>
        )}

        {busy ? (
          <div className="flex flex-col items-center py-8">
            <div className="w-8 h-8 rounded-full border-2 animate-spin border-zinc-700 border-t-white" />
            <p className="mt-4 text-sm text-zinc-500">
              {status === 'redirecting' ? 'Открываем Яндекс ID…' : 'Завершаем вход…'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              onClick={handleStart}
              className="w-full flex items-center justify-center px-4 py-3.5 rounded-xl bg-[#FC3F1D] text-on-accent text-sm font-medium hover:bg-[#e63515] transition-colors"
            >
              <YandexMark className="mr-2 w-6 h-6 text-sm rounded-lg bg-black/20" />
              {errorText ? 'Попробовать снова' : 'Продолжить с Яндекс ID'}
            </button>

            <p className="text-xs leading-relaxed text-center text-zinc-500">
              Откроется страница Яндекса. После подтверждения вы вернётесь сюда — вход завершится
              автоматически.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default YandexAuth;
